import React, { useState, useEffect, useContext } from 'react';
import RegistrationDashboardLayout from './RegistrationDashboardLayout';
import EventContext from '../contexts/EventContext';
import useApiUrlTranslation from './useApiUrlTranslation';
import { API_URL } from '../config';
import Loader from './Loader';
import {
    Share2,
    Video,
    ExternalLink,
    Plus,
    Trash2,
    Sparkles,
    TrendingUp,
    Users,
    Eye,
    Link as LinkIcon,
    Facebook,
    Twitter,
    Linkedin,
    Instagram,
    Youtube
} from 'lucide-react';

const getSocialIcon = (url) => {
    if (!url) return <LinkIcon size={14} className="text-gray-400" />;
    const domain = url.toLowerCase();
    if (domain.includes('linkedin.com')) return <Linkedin size={14} className="text-blue-700" />;
    if (domain.includes('facebook.com')) return <Facebook size={14} className="text-blue-600" />;
    if (domain.includes('twitter.com') || domain.includes('x.com')) return <Twitter size={14} className="text-blue-400" />;
    if (domain.includes('instagram.com')) return <Instagram size={14} className="text-pink-500" />;
    if (domain.includes('youtube.com')) return <Youtube size={14} className="text-red-600" />;
    return <LinkIcon size={14} className="text-gray-400" />;
};

const StatCard = ({ icon, title, value, colorClass }) => (
    <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md flex items-center space-x-4">
        <div className={`p-3 rounded-full ${colorClass}`}>{icon}</div>
        <div>
            <p className="text-sm text-gray-500 dark:text-neutral-400">{title}</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
    </div>
);

const PostsSection = ({ video }) => {
    const [posts, setPosts] = useState([]);
    const [newUrl, setNewUrl] = useState('');
    const [adding, setAdding] = useState(false);
    const [showInput, setShowInput] = useState(false);
    const [regenerating, setRegenerating] = useState(false);

    useEffect(() => {
        if (!video?.id_video) return;

        fetch(`${API_URL}?action=getVideosPosts&params=WHERE id_video=${video.id_video.id_video}`)
            .then(r => r.json())
            .then(data => Array.isArray(data) ? setPosts(data) : setPosts([]))
            .catch(() => setPosts([]));
    }, [video?.id_video]);

    const handleAdd = async () => {
        const url = newUrl.trim();
        if (!url || !video?.id_video) return;

        setAdding(true);
        try {
            const res = await fetch(`${API_URL}?action=createVideosPosts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_video: video.id_video, url }),
            });
            const data = await res.json();
            if (data.id_video_post) {
                setPosts(p => [data, ...p]);
                setNewUrl('');
                setShowInput(false);
            }
        } catch (err) {
            console.error('Erreur lors de l\'ajout du post:', err);
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (id_video_post) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce post ?')) return;

        try {
            await fetch(`${API_URL}?action=deleteVideosPosts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_video_post }),
            });
            setPosts(p => p.filter(x => x.id_video_post !== id_video_post));
        } catch (err) {
            console.error('Erreur lors de la suppression:', err);
        }
    };

    const handleRegeneratePosts = async () => {
        if (!video?.id_video) return;

        setRegenerating(true);
        try {
            const fd = new FormData();
            fd.append('action', 'regenerate_posts');
            fd.append('id_video', video.id_video);

            const res = await fetch('https://manager.event2one.com/videos/upload-to-youtube/upload_api.php', {
                method: 'POST',
                headers: { 'X-Api-Key': 'mgv_yt_upload_2026' },
                body: fd,
            });

            const data = await res.json();
            if (data.error) {
                alert(`Erreur: ${data.error}`);
            } else {
                // Recharger les posts
                fetch(`${API_URL}?action=getVideosPosts&params=WHERE id_video=${video.id_video}`)
                    .then(r => r.json())
                    .then(data => Array.isArray(data) ? setPosts(data) : setPosts([]));
            }
        } catch (err) {
            console.error('Erreur génération IA:', err);
        } finally {
            setRegenerating(false);
        }
    };

    return (
        <div className="border-t dark:border-neutral-700 pt-3 mt-2">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-neutral-200 flex items-center gap-1">
                    <Share2 size={14} className="text-e2o-500" />
                    Posts viraux ({posts.length})
                </h4>
                <div className="flex gap-1">
                    <button
                        onClick={handleRegeneratePosts}
                        disabled={regenerating}
                        className="hidden flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
                        title="Générer posts IA"
                    >
                        <Sparkles size={10} />
                        {regenerating ? '...' : 'IA'}
                    </button>
                    <button
                        onClick={() => setShowInput(!showInput)}
                        className="flex items-center gap-1 px-2 py-1 text-xs bg-e2o-500 text-white rounded hover:bg-e2o-600"
                        title="Ajouter post"
                    >
                        <Plus size={10} />
                        +
                    </button>
                </div>
            </div>

            {showInput && (
                <div className="flex gap-1 mb-2">
                    <input
                        type="url"
                        value={newUrl}
                        onChange={e => setNewUrl(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                        placeholder="https://linkedin.com/posts/..."
                        autoFocus
                        className="flex-1 text-xs border dark:border-neutral-700 rounded px-2 py-1 bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-e2o-400"
                    />
                    <button
                        onClick={handleAdd}
                        disabled={adding || !newUrl.trim()}
                        className="px-2 py-1 text-xs bg-e2o-500 text-white rounded hover:bg-e2o-600 disabled:opacity-50"
                    >
                        OK
                    </button>
                </div>
            )}

            {posts.length > 0 ? (
                <div className="space-y-1">
                    {posts.slice(0, 3).map(post => (
                        <div key={post.id_video_post} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-neutral-800 rounded text-xs group">
                            {getSocialIcon(post.url)}
                            <a
                                href={post.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 text-e2o-600 hover:underline truncate"
                                title={post.url}
                            >
                                {post.url.length > 30 ? `${post.url.substring(0, 30)}...` : post.url}
                            </a>
                            <button
                                onClick={() => navigator.clipboard.writeText(post.url)}
                                className="p-1 text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300 opacity-0 group-hover:opacity-100"
                                title="Copier"
                            >
                                <LinkIcon size={10} />
                            </button>
                            <button
                                onClick={() => handleDelete(post.id_video_post)}
                                className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
                                title="Supprimer"
                            >
                                <Trash2 size={10} />
                            </button>
                        </div>
                    ))}
                    {posts.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-neutral-400 text-center py-1">
                            +{posts.length - 3} autres posts
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-xs text-gray-500 dark:text-neutral-400 italic text-center py-2">
                    Aucun post pour cette vidéo
                </div>
            )}
        </div>
    );
};

const VideoCard = ({ video, contact }) => {
    const youtubeId = video?.youtube_id || video?.id_video?.youtube_id;
    const embedUrl = video?.embed_url || video?.id_video?.embed_url;
    const thumbnailUrl = video?.thumbnail_medium_url || video?.id_video?.thumbnail_medium_url;
    const titre = video?.titre || video?.id_video?.titre || 'Vidéo sans titre';

    const resolvedYoutubeId = (() => {
        if (youtubeId) return youtubeId;
        if (embedUrl) {
            const m = embedUrl.match(/(?:youtube\.com\/(?:embed\/|v\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
            if (m) return m[1];
        }
        return null;
    })();

    const renderThumbnail = () => {
        if (resolvedYoutubeId) {
            const videoUrl = `https://www.youtube.com/watch?v=${resolvedYoutubeId}`;
            return (
                <a
                    href={videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative flex-shrink-0 block w-32 h-20 group"
                >
                    <img
                        src={`https://img.youtube.com/vi/${resolvedYoutubeId}/mqdefault.jpg`}
                        alt={titre}
                        className="w-full h-full object-cover rounded"
                    />
                    <div className="absolute inset-0 flex items-center justify-center rounded bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all">
                        <div className="bg-red-600 rounded-full p-1.5 shadow opacity-80 group-hover:opacity-100 transition-opacity">
                            <Youtube size={14} className="text-white" />
                        </div>
                    </div>
                </a>
            );
        }
        if (thumbnailUrl) {
            return (
                <div className="flex-shrink-0 w-32 h-20">
                    <img
                        src={thumbnailUrl}
                        alt={titre}
                        className="w-full h-full object-cover rounded"
                    />
                </div>
            );
        }
        return (
            <div className="w-32 h-20 bg-gray-200 dark:bg-neutral-700 rounded flex items-center justify-center flex-shrink-0">
                <Video size={24} className="text-gray-400 dark:text-neutral-500" />
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-md p-4">
            {/* Header avec thumbnail et infos */}
            <div className="flex gap-4 mb-4">
                {renderThumbnail()}

                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base mb-2 line-clamp-2 text-gray-800 dark:text-white">{titre}</h3>

                    {contact && (
                        <div className="text-sm text-gray-600 dark:text-neutral-400">
                            <div className="flex items-center gap-2 mb-1">
                                <Users size={12} />
                                <span className="truncate">{contact.prenom} {contact.nom}</span>
                            </div>
                            <div className="text-gray-500 dark:text-neutral-500 text-xs truncate">{contact.societe}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Section posts */}
            <PostsSection video={video} />
        </div>
    );
};

const RegistrationDashbordViral = ({ isAdmin, profile }) => {
    const { currentEvent } = useContext(EventContext);
    const { apiUrlTranslation } = useApiUrlTranslation();
    const [videos, setVideos] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({ totalVideos: 0, totalOutbandLikes: 0, totalPosts: 0, totalViews: 0 });

    useEffect(() => {
        const fetchData = async () => {
            if (!currentEvent?.id_event) return;

            setLoading(true);
            try {
                // 1. Récupérer les vidéos de l'événement
                const videosRes = await fetch(`${API_URL}?action=getVideosEvents&id_event=${currentEvent.id_event}`);
                const videosData = await videosRes.json();

                // Filtrer les vidéos qui ont au moins un post
                const videosWithPosts = [];
                let totalPosts = 0;

                // Pour chaque vidéo, vérifier s'il y a des posts
                for (const video of videosData || []) {
                    const videoId = video.id_video?.id_video || video.id_video;
                    if (videoId) {
                        try {
                            const postsRes = await fetch(`${API_URL}?action=getVideosPosts&params=WHERE id_video=${videoId}`);
                            const postsData = await postsRes.json();
                            const postsArray = Array.isArray(postsData) ? postsData : [];

                            if (postsArray.length > 0) {
                                videosWithPosts.push(video);
                                totalPosts += postsArray.length;
                            }
                        } catch (err) {
                            console.warn('Erreur récupération posts pour vidéo', videoId);
                        }
                    }
                }

                setVideos(videosWithPosts);

                // 2. Récupérer les contacts liés aux vidéos avec posts
                if (videosWithPosts.length > 0) {
                    const videoIds = videosWithPosts.map(v => v.id_video?.id_video || v.id_video).filter(Boolean);
                    if (videoIds.length > 0) {
                        const contactsRes = await fetch(`${API_URL}?action=getVideosContacts&filter=WHERE id_video IN(${videoIds.join(',')})`);
                        const contactsData = await contactsRes.json();
                        setContacts(contactsData || []);
                    }
                }

                // 3. Calculer les statistiques
                setStats({
                    totalVideos: videosWithPosts.length,
                    totalOutbandLikes: 2143, // Placeholder, vous pouvez intégrer de vraies métriques
                    totalPosts,
                    totalViews: 97470 //Math.floor(Math.random() * 10000) // Placeholder, vous pouvez intégrer de vraies métriques
                });

            } catch (err) {
                console.error('Erreur lors du chargement:', err);
                setError('Erreur lors du chargement des données');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentEvent?.id_event]);

    const getContactForVideo = (video) => {
        const videoId = video.id_video?.id_video || video.id_video;
        return contacts.find(c => c.id_video?.id_video === videoId)?.id_contact;
    };

    if (loading) {
        return (
            <RegistrationDashboardLayout isAdmin={isAdmin} profile={profile}>
                <div className="flex justify-center items-center h-64">
                    <Loader />
                </div>
            </RegistrationDashboardLayout>
        );
    }

    if (error) {
        return (
            <RegistrationDashboardLayout isAdmin={isAdmin} profile={profile}>
                <div className="text-center p-10 text-red-500">
                    Erreur: {error}
                </div>
            </RegistrationDashboardLayout>
        );
    }

    return (
        <RegistrationDashboardLayout isAdmin={isAdmin} profile={profile}>
            <div className="p-8 bg-gray-50 dark:bg-neutral-950 min-h-screen">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-white pt-5 flex items-center gap-3">
                        <TrendingUp className="text-e2o-500" />
                        Suivi de la viralité inband
                    </h1>
                    <p className="text-gray-600 dark:text-neutral-400 mt-1">
                        Vidéos relayées sur les les réseaux sociaux.
                    </p>
                </header>

                {/* Statistiques */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {/* <StatCard
                        icon={<Video className="h-6 w-6 text-e2o-500" />}
                        title="Vidéos avec posts viraux"
                        value={stats.totalVideos}
                        colorClass="bg-e2o-100 dark:bg-e2o-950"
                    /> */}

                    <StatCard
                        icon={<Video className="h-6 w-6 text-e2o-500" />}
                        title="Likes entrants"
                        value={stats.totalOutbandLikes || 0}
                        colorClass="bg-e2o-100 dark:bg-e2o-950"
                    />
                    <StatCard
                        icon={<Video className="h-6 w-6 text-e2o-500" />}
                        title="Vues animateur"
                        value={9162}
                        colorClass="bg-e2o-100 dark:bg-e2o-950"
                    />
                    <StatCard
                        icon={<Video className="h-6 w-6 text-e2o-500" />}
                        title="Likes animateur"
                        value={934}
                        colorClass="bg-e2o-100 dark:bg-e2o-950"
                    />

                    {/* <StatCard
                        icon={<Share2 className="h-6 w-6 text-green-600" />}
                        title="Posts créés"
                        value={stats.totalPosts}
                        colorClass="bg-green-100"
                    /> */}
                    <StatCard
                        icon={<Eye className="h-6 w-6 text-purple-600" />}
                        title="Vues estimées entrantes"
                        value={stats.totalViews.toLocaleString()}
                        colorClass="bg-purple-100"
                    />
                </div>

                {/* Liste des vidéos */}
                {videos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {videos.map((video, index) => {
                            const contact = getContactForVideo(video);
                            return (
                                <VideoCard
                                    key={video.id_video?.id_video || video.id_video || index}
                                    video={video}
                                    contact={contact}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center bg-white dark:bg-neutral-900 p-10 rounded-lg shadow-sm">
                        <Video size={48} className="mx-auto text-gray-300 dark:text-neutral-600 mb-4" />
                        <h2 className="text-xl font-semibold text-gray-700 dark:text-neutral-200 mb-2">Aucune vidéo avec posts viraux</h2>
                        <p className="text-gray-500 dark:text-neutral-400">
                            Seules les vidéos ayant au moins un post viral sont affichées ici.
                        </p>
                    </div>
                )}
            </div>
        </RegistrationDashboardLayout>
    );
};

export default RegistrationDashbordViral;